import { useRef, useState } from 'react'
import { useForm, router } from '@inertiajs/react'
import PriorityDropdown from './PriorityDropdown'
import CategoryDropdown from './CategoryDropdown'
import ConfirmModal from '../shared/ConfirmModal'
import SidePanel from '../shared/SidePanel'
import MeetingTopicActions from '../meetings/MeetingTopicActions'
import type { Topic, TopicFilters, User } from '../../types'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  teamId: number
  teamMembers: User[]
  topic?: Topic
  meetingId?: number
  filters?: TopicFilters
  canDelete?: boolean
  onClose: () => void
  onResolve?: () => void
  // Receives the current (possibly unsaved) form values so the conversion
  // modal can be seeded with what the user just typed, not the stale topic.
  onConvert?: (draft: { title: string; description: string }) => void
}

const inputClass =
  'w-full px-4 py-3 border border-[#351E1D]/20 rounded-lg text-text-primary focus:outline-none focus:border-charcoal focus:ring-2 focus:ring-charcoal/15'
const selectClass =
  'w-full px-3 py-2 border border-[#351E1D]/20 rounded-lg text-text-primary focus:outline-none focus:border-charcoal focus:ring-2 focus:ring-charcoal/15'
const labelClass = 'font-bold uppercase tracking-widest text-text-primary mb-2 block'

/**
 * Topic create/edit form.
 *
 * Create mode stays a centered modal. Edit mode (Task #649) renders in a
 * non-blocking right-hand side panel so the rest of the page — navigation,
 * tabs, + Add Topic, + Action Item, other topic rows — stays clickable while
 * a topic is open, and no stray click can discard typed edits.
 */
export default function TopicModal({ teamId, teamMembers, topic, meetingId, filters, canDelete = true, onClose, onResolve, onConvert }: Props) {
  const isEditing = !!topic
  const canAct = isEditing && topic.status !== 'resolved'
  const titleInputRef = useRef<HTMLInputElement>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const createdDate = topic?.created_at
    ? new Date(topic.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null

  const { data, setData, post, put, processing, errors: rawErrors } = useForm({
    topic: {
      title: topic?.title || '',
      description: topic?.description || '',
      category: topic?.category || '',
      priority: topic?.priority || false,
      owner_id: topic?.owner?.id || (teamMembers[0]?.id || ''),
      topic_type: topic?.topic_type || 'short_term',
    },
    ...(filters ? { filters } : {}),
  })

  // The server returns validation errors keyed by plain attribute name
  // (e.g. `title`), not by the nested form key (`topic.title`).
  const errors = rawErrors as Record<string, string>

  const basePath = meetingId ? `/meetings/${meetingId}/topics` : `/teams/${teamId}/topics`

  // Unique so an open edit panel and the create modal can coexist without
  // their <form> ids colliding (the panel no longer blocks the page).
  const formId = `topic-form-${topic?.id ?? 'new'}`

  const submitCreate = (createAnother: boolean) => {
    post(basePath, {
      preserveScroll: true,
      onSuccess: () => {
        if (createAnother) {
          setData('topic', { ...data.topic, title: '', description: '' })
          titleInputRef.current?.focus()
        } else {
          onClose()
        }
      },
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isEditing) {
      put(`${basePath}/${topic.id}`, { onSuccess: onClose, preserveScroll: true })
    } else {
      submitCreate(false)
    }
  }

  const updateField = (field: string, value: any) => {
    setData('topic', { ...data.topic, [field]: value })
  }

  const titleField = (
    <div>
      <input
        ref={titleInputRef}
        type="text"
        placeholder="Topic title"
        value={data.topic.title}
        onChange={e => updateField('title', e.target.value)}
        className={inputClass}
        style={{ fontSize: '16px' }}
        autoFocus
      />
      {errors.title && (
        <p className="text-[#ED1C24] mt-1" style={{ fontSize: '14px' }}>{errors.title}</p>
      )}
    </div>
  )

  const descriptionField = (rows: number) => (
    <textarea
      placeholder="Add a description..."
      value={data.topic.description}
      onChange={e => updateField('description', e.target.value)}
      rows={rows}
      className={`${inputClass} resize-y`}
      style={{ fontSize: '16px' }}
    />
  )

  const ownerField = (
    <div>
      <label className={labelClass} style={{ fontSize: '12px' }}>Owner</label>
      <select
        value={data.topic.owner_id}
        onChange={e => updateField('owner_id', Number(e.target.value))}
        className={selectClass}
        style={{ fontSize: '16px' }}
      >
        <option value="">Select owner</option>
        {topic?.owner && !teamMembers.some(m => m.id === topic.owner?.id) && <option value={topic.owner.id} disabled>{topic.owner.first_name} {topic.owner.last_name} (Current owner)</option>}
        {sortPeople(teamMembers).map(m => (
          <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
        ))}
      </select>
    </div>
  )

  const priorityField = (
    <div>
      <label className={labelClass} style={{ fontSize: '12px' }}>Priority</label>
      <PriorityDropdown
        value={data.topic.priority}
        onChange={(p) => updateField('priority', p)}
        expanded
      />
    </div>
  )

  const categoryField = (
    <div>
      <label className={labelClass} style={{ fontSize: '12px' }}>Category</label>
      <CategoryDropdown
        value={data.topic.category}
        onChange={(c) => updateField('category', c)}
      />
    </div>
  )

  const topicTypeField = (
    <div>
      <label className={labelClass} style={{ fontSize: '12px' }}>Topic Type</label>
      <select
        value={data.topic.topic_type}
        onChange={e => updateField('topic_type', e.target.value)}
        className={selectClass}
        style={{ fontSize: '16px' }}
      >
        <option value="short_term">Short Term</option>
        <option value="long_term">Camp</option>
      </select>
    </div>
  )

  const createdField = isEditing && createdDate && (
    <div>
      <label className={labelClass} style={{ fontSize: '12px' }}>Created</label>
      <p className="text-text-secondary" style={{ fontSize: '14px' }}>{createdDate}</p>
    </div>
  )

  const solvedButton = canAct && onResolve && (
    <button
      type="button"
      onClick={onResolve}
      className="flex items-center gap-1.5 px-4 py-2 rounded-lg font-semibold transition-colors bg-[#15532E] text-white hover:bg-[#15532E]/90"
      style={{ fontSize: '14px' }}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      Solved
    </button>
  )

  const convertButton = canAct && onConvert && (
    <button
      type="button"
      onClick={() => onConvert({ title: data.topic.title, description: data.topic.description })}
      className="flex items-center gap-1.5 px-4 py-2 rounded-lg font-semibold transition-colors bg-gray-200 text-[#1B1B1B] hover:bg-gray-300"
      style={{ fontSize: '14px' }}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
      Convert to Action Item
    </button>
  )

  const deleteButton = isEditing && canDelete && (
    <button
      type="button"
      onClick={() => setShowDeleteConfirm(true)}
      className="flex items-center gap-1.5 px-4 py-2 rounded-lg font-semibold transition-colors bg-white text-[#ED1C24] border border-[#ED1C24]/30 hover:bg-[#ED1C24]/5"
      style={{ fontSize: '14px' }}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
      Delete
    </button>
  )

  const hasSecondaryActions = !!(solvedButton || convertButton || deleteButton)

  const cancelButton = (
    <button type="button" onClick={onClose}
      className="px-4 py-2 font-semibold text-text-primary hover:bg-[#351E1D]/5 rounded-lg transition-colors"
      style={{ fontSize: '16px' }}>
      Cancel
    </button>
  )

  const saveButton = (
    <button type="submit" form={formId} disabled={processing}
      className="bg-[#ED1C24] text-white px-5 py-2 rounded-lg font-semibold hover:bg-pinnacle-red-dark disabled:opacity-50 transition-colors"
      style={{ fontSize: '16px' }}>
      {processing ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Topic')}
    </button>
  )

  const deleteConfirm = isEditing && (
    <ConfirmModal
      isOpen={showDeleteConfirm}
      title="Delete this topic?"
      message="This permanently removes the topic. This cannot be undone."
      confirmLabel="Delete"
      cancelLabel="Cancel"
      variant="danger"
      submitting={deleting}
      onConfirm={() => {
        setDeleting(true)
        router.delete(`${basePath}/${topic.id}`, {
          preserveScroll: true,
          onSuccess: () => {
            setShowDeleteConfirm(false)
            onClose()
          },
          onFinish: () => setDeleting(false),
        })
      }}
      onClose={() => setShowDeleteConfirm(false)}
    />
  )

  if (isEditing) {
    // Non-blocking side panel: no backdrop, closes only via X / Cancel or a
    // successful save, delete, solve or convert.
    return (
      <>
        <SidePanel
          isOpen
          title="Edit Topic"
          headerActions={meetingId ? <MeetingTopicActions /> : undefined}
          onClose={onClose}
          footer={
            <div className="flex flex-col gap-3">
              {hasSecondaryActions && (
                <div className="flex items-center gap-2 flex-wrap">
                  {solvedButton}
                  {convertButton}
                  {deleteButton}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 flex-wrap">
                {cancelButton}
                {saveButton}
              </div>
            </div>
          }
        >
          <form id={formId} onSubmit={handleSubmit} className="p-6 space-y-5">
            {titleField}
            {descriptionField(6)}
            {ownerField}
            {priorityField}
            {categoryField}
            {topicTypeField}
            {createdField}
          </form>
        </SidePanel>

        {deleteConfirm}
      </>
    )
  }

  return (
    <div className="fixed inset-0 bg-[#351E1D]/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <form id={formId} onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#351E1D]/10 flex-shrink-0">
            <h2 className="font-semibold text-text-primary" style={{ fontSize: '18px' }}>
              Add Topic
            </h2>
            <button type="button" onClick={onClose} className="text-text-secondary hover:text-text-primary transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="flex">
              <div className="flex-1 p-6 border-r border-[#351E1D]/10 space-y-4">
                {titleField}
                <div>{descriptionField(8)}</div>
              </div>

              <div className="w-64 p-6 space-y-5">
                {ownerField}
                {priorityField}
                {categoryField}
                {topicTypeField}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 px-6 py-4 border-t border-[#351E1D]/10 flex-shrink-0">
            <div className="flex-1" />
            {cancelButton}
            <button type="button" disabled={processing}
              onClick={() => submitCreate(true)}
              className="px-4 py-2 font-semibold text-[#351E1D] border border-[#351E1D]/20 hover:bg-[#351E1D]/5 rounded-lg disabled:opacity-50 transition-colors"
              style={{ fontSize: '16px' }}>
              {processing ? 'Saving...' : 'Save and Create Another'}
            </button>
            {saveButton}
          </div>
        </form>
      </div>
    </div>
  )
}
